import {useIsFeatureStateEnabled} from '../../hooks/use-feature-state'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {AlertIcon} from '@primer/octicons-react'
import {Button, IconButton, Tooltip} from '@primer/react'
import {useRef} from 'react'
import {clsx} from 'clsx'

import {CounterSkeleton} from './CounterSkeleton'
import {useAnnotations} from '../../stores/selectors'
import {useAnnotationsCount} from '../../stores/layout-selectors'
import {usePullRequestAnalytics} from '../../hooks/use-pull-request-analytics'
import {DockedPanel, useDockedPanelActions, useIsDockedPanelOpen} from '../../contexts/DockedPanelContext'

export interface OpenAlertsPanelButtonProps {
  className?: string
  hideIfNoAlerts?: boolean
  buttonClasses?: string
  showMarkerCounts?: boolean
  size?: 'small' | 'medium'
}

/**
 *
 * Renders a button that opens the alerts panel
 * Contains the total number of alerts plus the icon
 */
export function OpenAlertsPanelButton({
  className,
  hideIfNoAlerts,
  buttonClasses,
  showMarkerCounts = true,
  size = 'small',
}: OpenAlertsPanelButtonProps) {
  const isDockedPanelOpen = useIsDockedPanelOpen(DockedPanel.Alerts)

  const toggleSidePanelRef = useRef<HTMLButtonElement>(null)
  const actions = useDockedPanelActions()

  const alerts = useAnnotations()
  const storeAnnotationsCount = useAnnotationsCount()
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  const showUpdatedHeader = useIsFeatureStateEnabled('overview')
  const totalAlertsCount = showUpdatedHeader ? storeAnnotationsCount : alerts.length
  const isLoading = showUpdatedHeader && showMarkerCounts && storeAnnotationsCount === null
  const isSidePanelOpen = isDockedPanelOpen
  const alertsButtonAriaLabel = isDockedPanelOpen ? 'Close alerts panel' : 'Open alerts panel'

  if (hideIfNoAlerts && !totalAlertsCount) {
    return null
  }

  return (
    <div className={clsx('d-flex flex-items-center', className)}>
      <ErrorBoundary
        boundaryName="OpenAlertsPanelButton"
        fallback={
          <IconButton
            inactive
            aria-label="The alerts panel cannot currently be opened."
            icon={AlertIcon}
            size={size}
            variant="invisible"
          />
        }
      >
        <Tooltip ref={toggleSidePanelRef} text={alertsButtonAriaLabel} direction="sw" type="label">
          <Button
            className={buttonClasses}
            aria-expanded={isSidePanelOpen}
            count={showMarkerCounts ? (isLoading ? undefined : (totalAlertsCount ?? undefined)) : undefined}
            leadingVisual={AlertIcon}
            trailingVisual={isLoading ? CounterSkeleton : undefined}
            size={size}
            variant="invisible"
            onClick={() => {
              sendPullRequestAnalyticsEvent('alerts_side_panel.open', 'ALERTS_SIDE_PANEL_BUTTON')
              actions.togglePanel('alerts', {returnFocusTo: toggleSidePanelRef.current})
            }}
          />
        </Tooltip>
      </ErrorBoundary>
    </div>
  )
}
